/**
 * The classic top-down camera (GDD §3.2; concept L1455–L1460): high above and slightly behind the jet, looking
 * ahead; it follows a blend of the river's centerline ahead and the jet, breathes gently, and shakes with blasts.
 * Pure: writes into caller-owned vectors.
 */
import { config, type CameraProfile } from '../core/config.ts';
import { damp, lerp } from '../core/math.ts';
import type { Rng } from '../core/rng.ts';
import type { WorldProfile } from '../world/profiles.ts';
import type { FlightState } from '../sim/flight.ts';
import type { CameraRig, RigPose } from './rigs.ts';

export interface TopRig {
  /** Smoothed camera x. */
  camX: number;
  /** Blast shake level (0..1.6). */
  shake: number;
}

export interface CameraPose {
  pos: [number, number, number];
  target: [number, number, number];
}

// Concept shake constants (L968, L1185, L1458); GDD §3.2 asks for shake in proportion to blast size that decays
// within about 1 second: from the cap it settles in 1.6 / 1.4 ≈ 1.1 s.
const SHAKE_PER_BLAST = 0.5;
const SHAKE_MAX = 1.6;
const SHAKE_DECAY = 1.4;
const SHAKE_GAIN = 1.6;

export function createTopRig(flight: FlightState): TopRig {
  return { camX: flight.x, shake: 0 };
}

/** Adds shake for a blast of the given size (the concept's boom size: 0.9 boat … 2.2 bridge). */
export function addShake(rig: TopRig, blastSize: number): void {
  rig.shake = Math.min(SHAKE_MAX, rig.shake + SHAKE_PER_BLAST * blastSize);
}

export function stepTopRig(rig: TopRig, flight: FlightState, W: WorldProfile, dt: number): void {
  const f = config.camera.follow;
  rig.camX = damp(rig.camX, f.riverShare * W.center(flight.z - f.riverAhead) + f.jetShare * flight.x, f.damping, dt);
  rig.shake = Math.max(0, rig.shake - dt * SHAKE_DECAY);
}

/** Camera position and look-at for the current state; `clock` drives the breathing, `rand` the shake jitter. */
export function topRigPose(
  rig: TopRig,
  flight: FlightState,
  p: CameraProfile,
  clock: number,
  rand: Rng,
  out: CameraPose,
): CameraPose {
  const b = config.camera.breathe;
  const shk = rig.shake * rig.shake * SHAKE_GAIN;
  const jx = shk > 0 ? (rand() - 0.5) * shk : 0;
  const jz = shk > 0 ? (rand() - 0.5) * shk : 0;
  out.pos[0] = rig.camX + jx;
  out.pos[1] = p.height + b.amp * Math.sin(clock * b.rate);
  out.pos[2] = flight.z + p.behind + jz;
  out.target[0] = rig.camX;
  out.target[1] = 0;
  out.target[2] = flight.z - p.lookAhead;
  return out;
}

/** The top-down rig behind the CameraRig interface; `profile` follows the screen shape (landscape / portrait). */
export interface TopCameraRig extends CameraRig {
  readonly state: TopRig;
  profile: CameraProfile;
}

export function createTopCameraRig(
  flight: FlightState,
  W: WorldProfile,
  profile: CameraProfile,
  rand: Rng,
): TopCameraRig {
  const state = createTopRig(flight);
  // Previous step and the interpolated rig, as objects (V8 updates their number fields in place).
  const prev = { camX: state.camX };
  const lerped: TopRig = { camX: state.camX, shake: 0 };
  const rig: TopCameraRig = {
    name: 'top',
    state,
    profile,
    step(f, dt) {
      prev.camX = state.camX;
      stepTopRig(state, f, W, dt);
    },
    shiftZ() {
      // The rig keeps only x; z comes from the jet.
    },
    cut(f) {
      const fo = config.camera.follow;
      state.camX = fo.riverShare * W.center(f.z - fo.riverAhead) + fo.jetShare * f.x;
      prev.camX = state.camX;
    },
    pose(f, alpha, clock, out: RigPose) {
      lerped.camX = lerp(prev.camX, state.camX, alpha);
      lerped.shake = state.shake;
      topRigPose(lerped, f, rig.profile, clock, rand, out);
      out.roll = 0;
      out.fov = rig.profile.fov;
      out.sun[0] = lerped.camX; // concept L1461
      out.sun[1] = f.z - 40;
      return out;
    },
  };
  return rig;
}
